import { cn } from '@/utils/styles';
import { Circle } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';

import type { StepGroup } from '@/features/OrderTrackingViewModels';

const formatDate = (dateString: string | null): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

interface OrderTrackingTimelineProps {
  stepGroups: StepGroup[];
  'data-testid'?: string;
}

export const OrderTrackingTimeline = ({
  stepGroups,
  'data-testid': testId,
}: OrderTrackingTimelineProps) => {
  const allSteps = stepGroups.flatMap((g) => g.steps);
  const totalSteps = allSteps.length;

  const containerRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [lineMetrics, setLineMetrics] = useState<{
    top: number;
    totalHeight: number;
    activeHeight: number;
  } | null>(null);

  const lastActiveGlobalIndex = (() => {
    let last = -1;
    allSteps.forEach((s, i) => {
      if (s.is_completed || s.is_active) last = i;
    });
    return last;
  })();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || dotRefs.current.length === 0) return;

    const firstDot = dotRefs.current[0];
    const lastDot = dotRefs.current[dotRefs.current.length - 1];
    if (!firstDot || !lastDot) return;

    const containerRect = container.getBoundingClientRect();
    const firstCenter =
      firstDot.getBoundingClientRect().top +
      firstDot.getBoundingClientRect().height / 2 -
      containerRect.top;
    const lastCenter =
      lastDot.getBoundingClientRect().top +
      lastDot.getBoundingClientRect().height / 2 -
      containerRect.top;

    let activeHeight = 0;
    if (lastActiveGlobalIndex >= 0) {
      const activeDot = dotRefs.current[lastActiveGlobalIndex];
      if (activeDot) {
        const activeCenter =
          activeDot.getBoundingClientRect().top +
          activeDot.getBoundingClientRect().height / 2 -
          containerRect.top;
        activeHeight = activeCenter - firstCenter;
      }
    }

    setLineMetrics({
      top: firstCenter,
      totalHeight: lastCenter - firstCenter,
      activeHeight,
    });
  }, [stepGroups, lastActiveGlobalIndex]);

  let globalStepIndex = 0;

  return (
    <div className="relative" data-testid={testId} ref={containerRef}>
      {lineMetrics && (
        <>
          <div
            className="absolute bg-repeat-y bg-gradient-to-b from-muted-foreground from-50% to-transparent to-50%"
            style={{
              left: 11,
              width: 2,
              top: lineMetrics.top,
              height: lineMetrics.totalHeight,
              backgroundSize: '2px 10px',
            }}
          />
          {lineMetrics.activeHeight > 0 && (
            <div
              className="absolute bg-repeat-y bg-gradient-to-b from-[#009E82] from-50% to-transparent to-50%"
              style={{
                left: 11,
                width: 2,
                top: lineMetrics.top,
                height: lineMetrics.activeHeight,
                backgroundSize: '2px 10px',
                zIndex: 1,
              }}
            />
          )}
        </>
      )}

      {stepGroups.map((group, groupIndex) => {
        const isGroupPending = group.steps.every(
          (s) => !s.is_active && !s.is_completed,
        );
        const isFirstGroup = groupIndex === 0;

        return (
          <div key={`${group.sectionTitle}-${groupIndex}`}>
            {group.sectionTitle !== 'Placed' && (
              <h3
                className={cn(
                  'typography-h2 ml-9',
                  isFirstGroup ? 'mt-2' : 'mt-6',
                  'mb-2',
                  isGroupPending ? 'text-tertiary' : 'text-primary',
                )}
              >
                {group.sectionTitle}
              </h3>
            )}

            {group.steps.map((step) => {
              const currentGlobalIndex = globalStepIndex++;
              const isLast = currentGlobalIndex === totalSteps - 1;
              const isActiveOrCompleted = step.is_active || step.is_completed;
              const formattedDate = formatDate(step.date);

              return (
                <div
                  key={step.ordinal}
                  className="flex items-stretch gap-3"
                  data-testid={
                    testId ? `${testId}-step-${step.ordinal}` : undefined
                  }
                >
                  <div className="flex flex-col items-center w-6">
                    <div
                      ref={(el) => {
                        dotRefs.current[currentGlobalIndex] = el;
                      }}
                      className="relative z-10 flex items-center justify-center w-6 h-6 flex-shrink-0"
                    >
                      {step.is_completed ? (
                        <Circle color="#009E82" fill="#009E82" size={8} />
                      ) : step.is_active ? (
                        <div className="relative">
                          <Circle color="#009E82" fill="#009E82" size={12} />
                          <Circle
                            color="#009E82"
                            fill="#009E82"
                            size={12}
                            className="absolute inset-0 animate-ping opacity-75"
                          />
                        </div>
                      ) : (
                        <Circle color="#A0A1AD" fill="#A0A1AD" size={10} />
                      )}
                    </div>
                  </div>

                  <div
                    className={cn(
                      'flex-1 self-start pt-0.5',
                      !isLast && 'pb-4',
                    )}
                  >
                    <p
                      className={cn(
                        'typography-body',
                        isActiveOrCompleted
                          ? 'text-primary font-bold'
                          : 'text-tertiary',
                      )}
                    >
                      {step.header_text}
                    </p>
                    {step.subheader_text && (
                      <p className="typography-caption1 font-light mt-2 text-tertiary">
                        {step.subheader_text}
                      </p>
                    )}
                    {formattedDate && (
                      <p className="typography-caption1 font-light text-tertiary mt-2">
                        {formattedDate}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
